import { useCallback, useState } from 'react';

export const COLOR_MODES = [
  { id: 'viridis', label: 'Viridis (Depth)' },
  { id: 'height', label: 'Height Ramp' },
  { id: 'rgb', label: 'RGB' },
  { id: 'intensity', label: 'Intensity' },
  { id: 'classification', label: 'Classification' },
];

export const DEFAULT_VIEW = {
  colorMode: 'viridis', threshold: [0, 1], sliceAxis: 'none', slicePos: 0.5, sliceThickness: 0.15, cropX: [0, 1], cropZ: [0, 1],
  renderMode: 'blocks', outsideOpacity: 0, showFences: true, fenceOpacity: 0.95, showVolumePanel: false,
  // scene elements (Layers panel 'Scene'): the reference grid is off by default
  showGrid: false, showAxes: true, showTraces: true, showSections: true, showVolumes: true, showBoreholes: true, showOverlay: true,
};

/**
 * View state of the 3D scene (colour mode, camera preset request, volume exploration filters, fence
 * display, terrain readiness), lifted out of the component that renders SubsurfaceViewer3D so the
 * /3d page's ribbon and the docked panel's controls drive the same props.
 */
export default function useViewer3dView() {
  const [colorMode, setColorMode] = useState('viridis');
  const [cameraPresetRequest, setCameraPresetRequest] = useState(null); // { id, seq }
  const onCameraPreset = useCallback((id) => {
    setCameraPresetRequest((prev) => ({ id, seq: (prev?.seq || 0) + 1 }));
  }, []);
  const [threshold, setThreshold] = useState([0, 1]);
  const [sliceAxis, setSliceAxis] = useState('none');
  const [slicePos, setSlicePos] = useState(0.5);
  const [sliceThickness, setSliceThickness] = useState(0.15);
  const [cropX, setCropX] = useState([0, 1]);
  const [cropZ, setCropZ] = useState([0, 1]);
  const [renderMode, setRenderMode] = useState('blocks'); // 3D models: 'blocks' | 'points' (blocks need cell extents)
  const [outsideOpacity, setOutsideOpacity] = useState(0); // opacity of cells outside the visible range
  const [showVolumePanel, setShowVolumePanel] = useState(false);
  const [showFences, setShowFences] = useState(true);
  const [fenceOpacity, setFenceOpacity] = useState(0.95);
  const [showGrid, setShowGrid] = useState(DEFAULT_VIEW.showGrid);
  const [showAxes, setShowAxes] = useState(DEFAULT_VIEW.showAxes);
  const [showTraces, setShowTraces] = useState(DEFAULT_VIEW.showTraces);
  const [showSections, setShowSections] = useState(DEFAULT_VIEW.showSections);
  const [showVolumes, setShowVolumes] = useState(DEFAULT_VIEW.showVolumes);
  const [showBoreholes, setShowBoreholes] = useState(DEFAULT_VIEW.showBoreholes);
  const [showOverlay, setShowOverlay] = useState(DEFAULT_VIEW.showOverlay);
  // DEM terrain (docs/DEM_TERRAIN.md): true once TerrainMesh draws the draped fences
  const [terrainReady, setTerrainReady] = useState(false);

  const resetVolumeFilters = useCallback(() => {
    setThreshold([0, 1]);
    setSliceAxis('none');
    setSlicePos(0.5);
    setSliceThickness(0.15);
    setCropX([0, 1]);
    setCropZ([0, 1]);
    setOutsideOpacity(0);
  }, []);

  // Saved view (lib/view-state.js): apply a validated partial state, or put every field back to its default
  const applyState = useCallback((v = {}) => {
    if (v.colorMode !== undefined) setColorMode(v.colorMode);
    if (v.threshold) setThreshold(v.threshold);
    if (v.sliceAxis !== undefined) setSliceAxis(v.sliceAxis);
    if (v.slicePos !== undefined) setSlicePos(v.slicePos);
    if (v.sliceThickness !== undefined) setSliceThickness(v.sliceThickness);
    if (v.cropX) setCropX(v.cropX);
    if (v.cropZ) setCropZ(v.cropZ);
    if (v.renderMode !== undefined) setRenderMode(v.renderMode);
    if (v.outsideOpacity !== undefined) setOutsideOpacity(v.outsideOpacity);
    if (v.showFences !== undefined) setShowFences(v.showFences);
    if (v.fenceOpacity !== undefined) setFenceOpacity(v.fenceOpacity);
    if (v.showVolumePanel !== undefined) setShowVolumePanel(v.showVolumePanel);
    if (v.showGrid !== undefined) setShowGrid(v.showGrid);
    if (v.showAxes !== undefined) setShowAxes(v.showAxes);
    if (v.showTraces !== undefined) setShowTraces(v.showTraces);
    if (v.showSections !== undefined) setShowSections(v.showSections);
    if (v.showVolumes !== undefined) setShowVolumes(v.showVolumes);
    if (v.showBoreholes !== undefined) setShowBoreholes(v.showBoreholes);
    if (v.showOverlay !== undefined) setShowOverlay(v.showOverlay);
  }, []);
  const resetViewState = useCallback(() => applyState(DEFAULT_VIEW), [applyState]);

  return {
    applyState, resetViewState,
    colorMode, setColorMode, cameraPresetRequest, onCameraPreset,
    threshold, setThreshold, sliceAxis, setSliceAxis, slicePos, setSlicePos, sliceThickness, setSliceThickness,
    cropX, setCropX, cropZ, setCropZ, renderMode, setRenderMode, outsideOpacity, setOutsideOpacity, showVolumePanel, setShowVolumePanel,
    showGrid, setShowGrid, showAxes, setShowAxes, showTraces, setShowTraces, showSections, setShowSections,
    showVolumes, setShowVolumes, showBoreholes, setShowBoreholes, showOverlay, setShowOverlay,
    showFences, setShowFences, fenceOpacity, setFenceOpacity, terrainReady, setTerrainReady,
    resetVolumeFilters,
  };
}
